import { useState } from 'react';
import { Box, Stack, TextField, Button, Chip } from '@mui/material';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable "add emails as chips" input. Used in New invoice ("Also send to")
 * and Email composer ("Also send to"). The caller owns the list — pass
 * `value` (array) and `onChange` (new array).
 *
 * Props:
 *   value, onChange       — required
 *   label, helperText     — TextField passthrough
 *   addLabel              — text on the Add button (default "Add")
 *   sx                    — wrapper Box style
 */
export default function EmailChipInput({
    value = [],
    onChange,
    label = 'Also send to (email)',
    helperText = 'Press Enter to add. Each email gets a copy.',
    addLabel = 'Add',
    sx,
}) {
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');

    const add = () => {
        const v = String(draft || '').trim().toLowerCase();
        if (!v) return;
        if (!EMAIL_RE.test(v)) {
            setError('Please enter a valid email address.');
            return;
        }
        if (value.includes(v)) {
            setDraft('');
            setError('');
            return;
        }
        onChange([...value, v]);
        setDraft('');
        setError('');
    };

    const remove = (email) => onChange(value.filter((e) => e !== email));

    return (
        <Box sx={sx}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                    label={label}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            add();
                        }
                    }}
                    size="small"
                    error={!!error}
                    helperText={error || helperText}
                    sx={{ flex: 1 }}
                />
                <Button onClick={add} sx={{ mt: '4px' }}>
                    {addLabel}
                </Button>
            </Stack>
            {value.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {value.map((e) => (
                        <Chip key={e} label={e} size="small" onDelete={() => remove(e)} />
                    ))}
                </Box>
            )}
        </Box>
    );
}
